/**
 * Central guards for dynamic package display (backend: package_kind, booking_state).
 * Source of truth: package_kind === "flights_new_proposal" when present; fallbacks for older rows.
 */

import { FLIGHTS_NEW_SOURCE_MODULE } from "../config/flightsModuleLds";

const PKG_KIND_FLIGHTS_NEW = "flights_new_proposal";

/** @returns {'flights_new_proposal'|'dynamic_hotel_flight'|'unknown'} */
export function getPackageDisplayMode(entity) {
  if (!entity || typeof entity !== "object") return "unknown";
  if (entity.package_kind === PKG_KIND_FLIGHTS_NEW) return "flights_new_proposal";
  if (entity.is_flights_new_flight_only === true) return "flights_new_proposal";
  const ci = entity.clientsinfo ?? entity.clientsInfo;
  if (ci && typeof ci === "object" && !Array.isArray(ci)) {
    const fp = ci.hrFlightPackage;
    if (
      fp &&
      typeof fp === "object" &&
      fp.source_module === FLIGHTS_NEW_SOURCE_MODULE &&
      fp.flight_only === true
    ) {
      return "flights_new_proposal";
    }
  }
  if (entity.package_kind === "dynamic_hotel_flight") return "dynamic_hotel_flight";
  if (entity.hotel != null && entity.ldsorderid != null && entity.orderid != null) {
    return "dynamic_hotel_flight";
  }
  if (entity.hotel != null && entity.ldsorderid != null) return "dynamic_hotel_flight";
  return "unknown";
}

export function isFlightsNewProposalPackage(entity) {
  return getPackageDisplayMode(entity) === "flights_new_proposal";
}

/** Display name used by the flight-only hotel stub — not a real hotel. */
export const FLIGHTS_NEW_PROPOSAL_STUB_HOTEL_NAME = "הצעת טיסה (PROPOSAL)";

/**
 * True when the "hotel" on a dynamic offer is only the flights_new PROPOSAL stub
 * (no real hotel to show in a package card).
 */
export function isSyntheticFlightsProposalHotel(hotelOrder, dynamicOrder) {
  if (hotelOrder?.isFlightsNewProposalStub === true) return true;
  if (isFlightsNewProposalPackage(dynamicOrder) || isFlightsNewProposalPackage(hotelOrder)) {
    return true;
  }
  const name = String(
    hotelOrder?.selectedHotel?.HotelName || hotelOrder?.selectedHotel?.hotelName || ""
  ).trim();
  return name === FLIGHTS_NEW_PROPOSAL_STUB_HOTEL_NAME;
}

/**
 * True when a hotel+flight package has a real hotel (name that is not the PROPOSAL stub).
 * Used to keep the "מלון כלול בחבילה" block off flight-only proposals.
 */
export function hasRealDynamicPackageHotel(hotelOrder, dynamicOrder) {
  if (isSyntheticFlightsProposalHotel(hotelOrder, dynamicOrder)) return false;
  const hotel =
    hotelOrder?.selectedHotel ||
    hotelOrder?.hotel ||
    dynamicOrder?.hotel ||
    dynamicOrder?.hotelOffer;
  if (!hotel || typeof hotel !== "object") return false;
  const name = String(hotel.HotelName || hotel.hotelName || hotel.name || "").trim();
  if (!name || name === "-" || name === FLIGHTS_NEW_PROPOSAL_STUB_HOTEL_NAME) return false;
  return true;
}

export function isProposalOnlyBookingState(entity) {
  if (!entity || typeof entity !== "object") return false;
  if (entity.booking_state === "proposal_only") return true;
  const ci = entity.clientsinfo ?? entity.clientsInfo;
  if (ci && typeof ci === "object" && ci.hrFlightPackage?.proposal_only === true) return true;
  return false;
}

/** Dev-only: warn when code assumes hotel on a flights_new proposal row */
export function devWarnUnexpectedHotelAccess(contextLabel, entity) {
  const isDev =
    (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.DEV) ||
    (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production");
  if (!isDev) return;
  if (!isFlightsNewProposalPackage(entity)) return;
  // eslint-disable-next-line no-console
  console.warn(
    `[flights_new proposal] ${contextLabel}: hotel-specific path invoked; render a flight-only branch instead.`,
    { id: entity?.id, package_kind: entity?.package_kind }
  );
}

export function safeDynamicHotel(entity) {
  if (isFlightsNewProposalPackage(entity)) {
    devWarnUnexpectedHotelAccess("safeDynamicHotel", entity);
    return null;
  }
  return entity?.hotel ?? null;
}

/** ILS numeric from LDS flights row */
export function extractDynamicFlightsTotalIls(flights) {
  if (!flights || typeof flights !== "object") return 0;
  const v =
    flights?.ourprice?.ourTotalFare?.priceWithCommissionAndCreditCardFee ??
    flights?.ourprice?.priceWithCreditCardFee ??
    flights?.ourTotalFare?.priceWithCommissionAndCreditCardFee;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * SSOT for flight-only proposal display from persisted LDS row (no re-pricing in UI).
 * Values are ILS as stored in ourprice.ourTotalFare / handlingfee.
 */
function numIls(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function getFlightsNewProposalPriceBreakdownIls(flights) {
  if (!flights || typeof flights !== "object") {
    return {
      baseIls: 0,
      handlingIls: 0,
      totalIls: 0,
    };
  }
  const tf = flights?.ourprice?.ourTotalFare;
  const tfObj = tf && typeof tf === "object" ? tf : null;
  const totalIls = numIls(
    tfObj?.priceWithCommissionAndCreditCardFee ??
      tfObj?.priceWithCreditCardFee ??
      flights?.ourprice?.priceWithCreditCardFee
  );
  const totalFallback = extractDynamicFlightsTotalIls(flights);
  const finalTotal = totalIls > 0 ? totalIls : totalFallback;
  const baseIls = numIls(tfObj?.priceWithCommission);
  const handlingIls = numIls(
    flights?.handlingfee ?? tfObj?.handleFee ?? tfObj?.handlingFee
  );
  const cardIls = numIls(tfObj?.creditCardFee);
  let displayBase = baseIls;
  if (!displayBase && finalTotal) {
    displayBase = Math.max(0, finalTotal - handlingIls - cardIls);
  }
  if (!displayBase) displayBase = finalTotal;
  return {
    baseIls: displayBase,
    handlingIls,
    totalIls: finalTotal || displayBase,
  };
}

/**
 * Total ILS for Summary table (orderReqData from buildOrderReqDataForFlightsNewDynamic) — public-safe, no context.
 */
export function getFlightsNewProposalDisplayIlsFromOrderReqData(entity) {
  if (!isFlightsNewProposalPackage(entity)) return null;
  const p =
    entity?.ourprice?.priceWithCreditCardFee ??
    entity?.pricetotal ??
    entity?.totalprice ??
    entity?.price;
  const n = Number(p);
  if (Number.isFinite(n) && n > 0) return n;
  return null;
}

/** PassangerList (editInfo=true) expects Gimmonix-shaped passengers */
export function ldsPassengersinfoToGimmonixPassengers(passengersinfo) {
  if (!Array.isArray(passengersinfo)) return [];
  const out = [];
  let idx = 0;
  for (const room of passengersinfo) {
    if (!room || typeof room !== "object") continue;
    for (const adult of room.adults || []) {
      if (!adult || typeof adult !== "object") continue;
      out.push({
        PersonDetails: {
          Name: {
            GivenName: String(adult.firstname || "").trim(),
            Surname: String(adult.lastname || "").trim(),
          },
          Age: null,
        },
        Id: String(adult["@seqNum"] ?? idx++),
      });
    }
    for (const kid of room.kids || []) {
      if (!kid || typeof kid !== "object") continue;
      out.push({
        PersonDetails: {
          Name: {
            GivenName: String(kid.firstname || "").trim(),
            Surname: String(kid.lastname || "").trim(),
          },
          Age: 10,
        },
        Id: String(kid["@seqNum"] ?? idx++),
      });
    }
    for (const inf of room.infants || []) {
      if (!inf || typeof inf !== "object") continue;
      out.push({
        PersonDetails: {
          Name: {
            GivenName: String(inf.firstname || "").trim(),
            Surname: String(inf.lastname || "").trim(),
          },
          Age: 1,
        },
        Id: String(inf["@seqNum"] ?? idx++),
      });
    }
  }
  return out;
}

/**
 * Synthetic orderReqData for payment/summary when dynamic row is flights_new (no Gimmonix hotel).
 */
export function buildOrderReqDataForFlightsNewDynamic(dynamicRow, flights) {
  const priceIls =
    extractDynamicFlightsTotalIls(flights) ||
    parseFloat(dynamicRow?.total_price) ||
    0.01;
  const linkedFlightsRaw = flights?.linkedflights ?? flights?.linkedFlights ?? null;
  let extractedRouteLabel = null;
  try {
    const f = Array.isArray(linkedFlightsRaw?.flight)
      ? linkedFlightsRaw.flight
      : Array.isArray(linkedFlightsRaw?.linkedFlights?.flight)
        ? linkedFlightsRaw.linkedFlights.flight
        : [];
    const first = f?.[0]?.leg;
    const last = f?.[f.length - 1]?.leg;
    if (first?.depCity && last?.arrCity) {
      extractedRouteLabel = `${first.depCity} → ${last.arrCity}`;
    }
  } catch {
    extractedRouteLabel = null;
  }
  const paxJson = JSON.stringify(ldsPassengersinfoToGimmonixPassengers(flights?.passengersinfo));
  const first = (flights?.passengersinfo || [])[0];
  const a0 = first?.adults?.[0];
  const fullname = a0 ? `${a0.firstname || ""} ${a0.lastname || ""}`.trim() : "";
  const ci = dynamicRow?.clientsinfo ?? dynamicRow?.clientsInfo;
  const fp = ci && typeof ci === "object" ? ci.hrFlightPackage : null;
  const btRaw = flights?.bank_transfer;
  const bankTransfer =
    btRaw === true ||
    btRaw === "true" ||
    btRaw === 1 ||
    fp?.payment_method === "bank_transfer";
  return {
    ...dynamicRow,
    deal_type: "Flights",
    package_kind: dynamicRow?.package_kind,
    booking_state: dynamicRow?.booking_state,
    is_flights_new_flight_only: dynamicRow?.is_flights_new_flight_only,
    bank_transfer: bankTransfer,
    lds_reservation_status: dynamicRow?.lds_reservation_status ?? null,
    ourprice: {
      priceWithCommission: priceIls,
      priceWithCreditCardFee: priceIls,
      handleFeeData: { type: "+", value: parseFloat(flights?.handlingfee) || 0 },
    },
    packagepricedetails: {
      FinalPrice: priceIls,
      Currency: dynamicRow?.currency || "ILS",
    },
    pricetotal: priceIls,
    totalprice: priceIls,
    price: priceIls,
    hotelid: null,
    hotelname: null,
    hoteladdress: null,
    rooms: [],
    checkin: dynamicRow?.check_in || "",
    checkout: dynamicRow?.check_out || "",
    check_in: dynamicRow?.check_in,
    check_out: dynamicRow?.check_out,
    passengers: paxJson,
    fullname: fullname || dynamicRow?.fullname || "",
    cell: a0?.cell || "",
    email: a0?.email || "",
    commission: flights?.commission ?? dynamicRow?.commission,
    linkedflights: linkedFlightsRaw,
    refundability: false,
    refundableuntil: null,
  };
}

export function getFlightsNewDynamicLoadError(dynamicRow, flights) {
  if (!dynamicRow?.id) return "request_failed";
  const p = extractDynamicFlightsTotalIls(flights);
  const t = parseFloat(dynamicRow.total_price);
  if ((!p || p <= 0) && (!t || t <= 0)) return "missing_total";
  return null;
}

/**
 * Map LDS flights.passengersinfo rooms → searchPhrase-style rooms (AdultsCount / KidsAges / InfantsCount)
 * for legacy components that expect Gimmonix hotel search phrase shape.
 */
export function ldsPassengersinfoToSearchPhraseRooms(passengersinfo) {
  if (!Array.isArray(passengersinfo)) return [];
  return passengersinfo.map((room) => {
    const adults = Array.isArray(room?.adults) ? room.adults.length : 0;
    const kids = Array.isArray(room?.kids) ? room.kids : [];
    const infants = Array.isArray(room?.infants) ? room.infants : [];
    return {
      AdultsCount: Math.max(adults, 1),
      KidsAges: kids.map(() => 10),
      InfantsCount: infants.length,
    };
  });
}

/**
 * Minimal "hotel order" stub so offer/passenger UI does not touch null selectedHotel.
 * Mark with isFlightsNewProposalStub; monetary fields are ILS in flight-only flow — paired UI must branch on isFlightsNewProposalPackage.
 */
export function buildSyntheticHotelOrderForFlightsOffer(dynamicRow, flights) {
  const priceIls =
    extractDynamicFlightsTotalIls(flights) || parseFloat(dynamicRow?.total_price) || 0.01;
  const checkIn = dynamicRow?.check_in || "";
  const checkOut = dynamicRow?.check_out || "";
  const rooms = ldsPassengersinfoToSearchPhraseRooms(flights?.passengersinfo);
  const pax = ldsPassengersinfoToGimmonixPassengers(flights?.passengersinfo);
  return {
    isFlightsNewProposalStub: true,
    selectedHotel: {
      HotelName: FLIGHTS_NEW_PROPOSAL_STUB_HOTEL_NAME,
      Address: "",
      Rating: 0,
      Result: [],
      images: [],
    },
    selectedHotelPackage: {
      packageID: dynamicRow?.id ?? 0,
      roomNames: [],
      roomBasis: "",
      refundability: false,
      refundableUntil: "",
      includesTaxes: false,
      ourPrice: {
        priceWithCommission: priceIls,
        priceWithCommissionAndCreditCardFee: priceIls,
        priceWithCreditCardFee: priceIls,
        handleFeeData: {
          type: "+",
          value: parseFloat(flights?.handlingfee ?? dynamicRow?.handlingfee) || 0,
        },
      },
    },
    selectedRoom: { roomNames: [], images: [] },
    searchPhrase: {
      checkIn,
      checkOut,
      hotelID: "",
      rooms: rooms.length ? rooms : [{ AdultsCount: 1, KidsAges: [], InfantsCount: 0 }],
      session: null,
    },
    passangerData: { passengerInfo: pax, specialReq: "" },
    ourPrice: {
      priceWithCommission: priceIls,
      priceWithCommissionAndCreditCardFee: priceIls,
      priceWithCreditCardFee: priceIls,
      handleFeeData: {
        type: "+",
        value: parseFloat(flights?.handlingfee ?? dynamicRow?.handlingfee) || 0,
      },
    },
    clientsInfo: dynamicRow?.clientsinfo || {},
  };
}
